import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, RefreshCw, CheckCircle, XCircle, AlertTriangle, Database, Zap } from 'lucide-react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { diagnostics, type DiagnosticResult } from '../utils/diagnostics';
import { databaseDebugger } from '../utils/databaseDebug';

export const DiagnosticsPanel: React.FC = () => {
  const [results, setResults] = useState<DiagnosticResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const runDiagnostics = async () => {
    setIsRunning(true);
    setResults([]);
    
    try {
      const diagnosticResults = await diagnostics.runAllDiagnostics();
      setResults(diagnosticResults);
    } catch (error) {
      console.error('Diagnostics failed:', error);
    } finally {
      setIsRunning(false);
    }
  };

  const runDatabaseDebug = async () => {
    setIsRunning(true);
    
    try {
      console.log('🔍 Running comprehensive database debug...');
      const debugResult = await databaseDebugger.runComprehensiveDebug();
      console.log('📊 Debug results:', debugResult);
      
      // If no data found, try direct insertion
      if (debugResult.rawDataCount.users === 0) {
        console.log('🚀 Attempting direct data insertion...');
        const insertResult = await databaseDebugger.insertTestDataDirectly();
        console.log('✅ Direct insertion result:', insertResult);
      }
    } catch (error) {
      console.error('❌ Database debug failed:', error);
    } finally {
      setIsRunning(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass':
        return <CheckCircle className="w-5 h-5 text-saavi-600" />;
      case 'fail':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-saavi-600" />;
      default:
        return <RefreshCw className="w-5 h-5 text-saavi-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pass':
        return 'border-saavi-200 bg-saavi-50';
      case 'fail':
        return 'border-red-200 bg-red-50';
      case 'warning':
        return 'border-saavi-200 bg-saavi-50';
      default:
        return 'border-saavi-200 bg-saavi-50';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">System Diagnostics</h2>
          <p className="text-gray-600">Comprehensive system health check</p>
        </div>
        
        <Button
          onClick={runDiagnostics}
          loading={isRunning}
          icon={<Search className="w-4 h-4 text-white" />}
        >
          Run Diagnostics
        </Button>
        
        <Button
          onClick={runDatabaseDebug}
          loading={isRunning}
          variant="secondary"
          icon={<Database className="w-4 h-4 text-white" />}
        >
          Debug Database
        </Button>
      </div>

      {results.length > 0 && (
        <div className="space-y-4">
          {results.map((result, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className={`border-2 ${getStatusColor(result.status)}`}>
                <div className="flex items-start space-x-3">
                  {getStatusIcon(result.status)}
                  
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{result.test}</h3>
                    <p className="text-gray-700 mt-1">{result.message}</p>
                    
                    {result.data && (
                      <details className="mt-3">
                        <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                          View Details
                        </summary>
                        <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-auto max-h-40">
                          {JSON.stringify(result.data, null, 2)}
                        </pre>
                      </details>
                    )}
                    
                    {/* Special handling for recommendations */}
                    {result.test === 'Recommendations' && result.data?.actions && (
                      <div className="mt-3 space-y-2">
                        <h4 className="text-sm font-medium text-gray-900">Suggested Actions:</h4>
                        <ul className="text-sm text-gray-700 space-y-1">
                          {result.data.actions.map((action: string, idx: number) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <span className="text-gray-400 mt-1">•</span>
                              <span>{action}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {isRunning && (
        <div className="text-center py-8">
          <RefreshCw className="w-8 h-8 animate-spin text-saavi-600 mx-auto mb-4" />
          <p className="text-gray-600">Running comprehensive diagnostics...</p>
        </div>
      )}

      {results.length === 0 && !isRunning && (
        <Card className="text-center py-8">
          <Search className="w-12 h-12 text-saavi-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Ready to Diagnose
          </h3>
          <p className="text-gray-600">
            Click "Run Diagnostics" to perform a comprehensive system health check
          </p>
        </Card>
      )}
    </div>
  );
};